import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQuery } from '@core/api/baseQuery';
import type { PaginatedResponse } from '@core/types/api';
import type {
  Conversation,
  ConversationEvent,
  ConversationSummary,
  CreateNotePayload,
  DeleteNotePayload,
  InternalNote,
  Message,
  RecaptureCase,
  SendMediaMessagePayload,
  SendMessagePayload,
  SetConversationAgentPayload,
  UpdateNotePayload,
} from '../types';

/**
 * Rows per page. Fixed, not a request parameter: the backend uses stock DRF
 * `PageNumberPagination` with `PAGE_SIZE = 20` and no `page_size_query_param`,
 * so sending one would be silently ignored. Only the pagination control needs
 * this, to turn `count` into a number of pages.
 */
export const PAGE_SIZE = 20;

/**
 * Polling intervals (ms). RTK Query takes `pollingInterval` per subscription (a
 * hook option), not in the endpoint definition, so consumers pass these to the
 * generated hooks: the open thread polls every 5s, and the inbox list + tab
 * counts refresh every 15s from a single shared interval on the page.
 */
export const THREAD_POLL_MS = 5000;
export const INBOX_POLL_MS = 15000;

/** Query parameters accepted by the conversation-list (inbox) endpoint. */
export interface ListConversationsParams {
  page?: number;
  /** Matched against the guest's first name, last name and phone. */
  search?: string;
  /** Filter by derived triage status (one of `ConversationStatus`). */
  status?: string;
  /** Only conversations that need a human (escalated/failed/pending). */
  attention?: boolean;
  /** Only archived conversations — its own bucket, not a filter on the inbox. */
  archived?: boolean;
  /** Only conversations that carry at least one internal note. */
  has_notes?: boolean;
  source?: 'recapture';
  stage?: RecaptureStage;
  days?: RecaptureDays;
}

export type RecaptureStage = 'sent' | 'read' | 'replied' | 'clicked' | 'recovered' | 'closed';
export type RecapturePeriod = 7 | 30 | 90 | 'all';
export type RecaptureDays = Exclude<RecapturePeriod, 'all'>;
export interface RecaptureFunnelParams {
  days?: RecaptureDays;
}
export interface RecaptureFunnel {
  sent: number;
  read: number;
  replied: number;
  clicked: number;
  recovered: number;
  closed: number;
  days: number | null;
}

/** Drop falsy values so we never send `?status=&attention=false&…`. */
function compactParams(
  params: ListConversationsParams | RecaptureFunnelParams,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === false || value === '') {
      continue;
    }
    out[key] = value === true ? 'true' : (value as string | number);
  }
  return out;
}

/**
 * RTK Query API for the WhatsApp inbox: conversations, their message timeline,
 * staff-only notes, the assistant toggle, and the recapture case behind each one.
 *
 * The shared `baseQuery` attaches the `Token` auth header and the active
 * `X-Organization` tenant header, so every request here is already org-scoped.
 * The API is registered in the store's `TENANT_SCOPED_APIS` list, so its cache is
 * wiped whenever the active organization changes.
 */
export const conversationsApi = createApi({
  reducerPath: 'conversationsApi',
  baseQuery,
  tagTypes: ['Conversation', 'Message', 'Note', 'Case'],
  endpoints: (builder) => ({
    /** The inbox list, polled so new rows and unread counts refresh. */
    listConversations: builder.query<
      PaginatedResponse<Conversation>,
      ListConversationsParams | void
    >({
      query: (params) => ({
        url: '/api/messaging/conversations/',
        method: 'GET',
        params: params ? compactParams(params) : undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ uuid }) => ({ type: 'Conversation' as const, id: uuid })),
              { type: 'Conversation' as const, id: 'LIST' },
            ]
          : [{ type: 'Conversation' as const, id: 'LIST' }],
    }),

    /**
     * Aggregate counts for the filter tabs (`total` / `attention` / per-status).
     * Tagged with the `Conversation` LIST id so it refetches whenever any
     * conversation changes (archive, assistant toggle, new message). Takes no
     * parameters — the backend always counts the whole active inbox.
     */
    getConversationSummary: builder.query<ConversationSummary, void>({
      query: () => ({
        url: '/api/messaging/conversations/summary/',
        method: 'GET',
      }),
      providesTags: [{ type: 'Conversation' as const, id: 'LIST' }],
    }),

    getRecaptureFunnel: builder.query<RecaptureFunnel, RecaptureFunnelParams | void>({
      query: (params) => ({
        url: '/api/messaging/conversations/recapture-funnel/',
        method: 'GET',
        params: params ? compactParams(params) : undefined,
      }),
      providesTags: [{ type: 'Conversation' as const, id: 'LIST' }],
    }),

    /** A single conversation — the header of an open thread. */
    getConversation: builder.query<Conversation, string>({
      query: (uuid) => ({
        url: `/api/messaging/conversations/${uuid}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, uuid) => [{ type: 'Conversation', id: uuid }],
    }),

    /** The message timeline of one conversation, polled while the thread is open. */
    listMessages: builder.query<PaginatedResponse<Message>, string>({
      query: (conversationUuid) => ({
        url: '/api/messaging/messages/',
        method: 'GET',
        params: { conversation: conversationUuid },
      }),
      providesTags: (result, _error, conversationUuid) =>
        result
          ? [
              ...result.results.map(({ uuid }) => ({ type: 'Message' as const, id: uuid })),
              { type: 'Message' as const, id: conversationUuid },
            ]
          : [{ type: 'Message' as const, id: conversationUuid }],
    }),

    /**
     * Send an outbound free-text reply.
     *
     * Optimistically appends a placeholder to the open thread's cache so the
     * composer feels instant, then invalidates the thread + inbox tags so the
     * server-persisted message (with its wamid and real status) replaces it. The
     * conversation travels in the BODY, not as a query parameter.
     *
     * Sending is also a human takeover: the backend pauses the assistant on this
     * conversation, which is why the conversation tag is invalidated too.
     */
    sendMessage: builder.mutation<Message, SendMessagePayload>({
      query: ({ conversationUuid, body }) => ({
        url: '/api/messaging/messages/',
        method: 'POST',
        body: { conversation: conversationUuid, body },
      }),
      async onQueryStarted({ conversationUuid, body }, { dispatch, queryFulfilled }) {
        const now = new Date().toISOString();
        const optimistic: Message = {
          uuid: `optimistic-${now}`,
          conversation_uuid: conversationUuid,
          direction: 'outbound',
          message_type: 'text',
          body,
          metadata: {},
          status: 'queued',
          wa_message_id: '',
          error: '',
          sent_by_name: null, // resolved on refetch (the real send carries the author)
          media: [],
          reactions: [],
          sent_at: null,
          delivered_at: null,
          read_at: null,
          failed_at: null,
          created_at: now,
        };
        const patch = dispatch(
          conversationsApi.util.updateQueryData('listMessages', conversationUuid, (draft) => {
            draft.results.push(optimistic);
            draft.count += 1;
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      // Sending is a takeover, and the backend audits it with a system note —
      // hence the Note tag alongside the thread and inbox ones.
      invalidatesTags: (_result, _error, { conversationUuid }) => [
        { type: 'Message', id: conversationUuid },
        { type: 'Conversation', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
        { type: 'Note', id: conversationUuid },
      ],
    }),

    /**
     * Send an image or PDF with an optional caption.
     *
     * The browser owns the multipart boundary. There is no optimistic bubble:
     * its authenticated preview URL only exists after the server has durably
     * saved the asset, so cache invalidation replaces the loading composer with
     * the real historical message.
     */
    sendMediaMessage: builder.mutation<Message, SendMediaMessagePayload>({
      query: ({ conversationUuid, file, body }) => {
        const formData = new FormData();
        formData.append('conversation', conversationUuid);
        formData.append('file', file);
        if (body) {
          formData.append('body', body);
        }
        return {
          url: '/api/messaging/messages/',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      invalidatesTags: (_result, _error, { conversationUuid }) => [
        { type: 'Message', id: conversationUuid },
        { type: 'Conversation', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
        { type: 'Note', id: conversationUuid },
      ],
    }),

    /**
     * Pause (`enabled: false`) or resume (`enabled: true`) the assistant on a
     * conversation. Resuming also clears any escalation server-side. Returns the
     * updated conversation, and invalidates it plus the inbox list so the thread
     * header and the table rows agree.
     */
    setConversationAgent: builder.mutation<Conversation, SetConversationAgentPayload>({
      query: ({ conversationUuid, enabled }) => ({
        url: `/api/messaging/conversations/${conversationUuid}/set-agent/`,
        method: 'POST',
        body: { enabled },
      }),
      // The Note tag goes too: the backend writes an audit note on every
      // pause/resume, and without this it only appeared after reopening the drawer.
      invalidatesTags: (_result, _error, { conversationUuid }) => [
        { type: 'Conversation', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
        { type: 'Note', id: conversationUuid },
      ],
    }),

    /** Mark a conversation read (zeroes its unread counter server-side). */
    markRead: builder.mutation<Conversation, string>({
      query: (conversationUuid) => ({
        url: `/api/messaging/conversations/${conversationUuid}/read/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, conversationUuid) => [
        { type: 'Conversation', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
      ],
    }),

    /**
     * Re-flag a conversation as unread — the undo for opening the wrong one.
     *
     * Opening a thread marks it read automatically, so without this there is no
     * way back to "I still need to deal with that" once you have glanced at it.
     */
    markUnread: builder.mutation<Conversation, string>({
      query: (conversationUuid) => ({
        url: `/api/messaging/conversations/${conversationUuid}/unread/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, conversationUuid) => [
        { type: 'Conversation', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
      ],
    }),

    /** Archive a conversation out of the active inbox. Returns its detail. */
    archiveConversation: builder.mutation<Conversation, string>({
      query: (conversationUuid) => ({
        url: `/api/messaging/conversations/${conversationUuid}/archive/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, conversationUuid) => [
        { type: 'Conversation', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
      ],
    }),

    /** Restore an archived conversation to the active inbox. Returns its detail. */
    unarchiveConversation: builder.mutation<Conversation, string>({
      query: (conversationUuid) => ({
        url: `/api/messaging/conversations/${conversationUuid}/unarchive/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, conversationUuid) => [
        { type: 'Conversation', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
      ],
    }),

    /** The staff-only internal notes attached to a conversation. */
    listNotes: builder.query<PaginatedResponse<InternalNote>, string>({
      query: (conversationUuid) => ({
        url: '/api/messaging/notes/',
        method: 'GET',
        params: { conversation: conversationUuid },
      }),
      providesTags: (result, _error, conversationUuid) =>
        result
          ? [
              ...result.results.map(({ uuid }) => ({ type: 'Note' as const, id: uuid })),
              { type: 'Note' as const, id: conversationUuid },
            ]
          : [{ type: 'Note' as const, id: conversationUuid }],
    }),

    /** Create an internal note (the backend forces `note_type: general`). */
    createNote: builder.mutation<InternalNote, CreateNotePayload>({
      query: ({ conversationUuid, body }) => ({
        url: '/api/messaging/notes/',
        method: 'POST',
        body: { conversation: conversationUuid, body },
      }),
      // The conversation LIST tag goes too, so the table's `notes_count` and the
      // "con notas" filter stay accurate.
      invalidatesTags: (_result, _error, { conversationUuid }) => [
        { type: 'Note', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
      ],
    }),

    /** Edit a `general` note's body. */
    updateNote: builder.mutation<InternalNote, UpdateNotePayload>({
      query: ({ uuid, body }) => ({
        url: `/api/messaging/notes/${uuid}/`,
        method: 'PATCH',
        body: { body },
      }),
      invalidatesTags: (_result, _error, { conversationUuid }) => [
        { type: 'Note', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
      ],
    }),

    /** Delete a `general` note. */
    deleteNote: builder.mutation<void, DeleteNotePayload>({
      query: ({ uuid }) => ({
        url: `/api/messaging/notes/${uuid}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { conversationUuid }) => [
        { type: 'Note', id: conversationUuid },
        { type: 'Conversation', id: 'LIST' },
      ],
    }),

    /**
     * Every beat the system recorded about one thread — staff actions, the
     * assistant's escalations, the cadence's steps — oldest first. Merged in the
     * "Cronología" tab with the case's own timestamps. Tagged with the
     * conversation so archiving, pausing or replying refreshes it.
     */
    listConversationEvents: builder.query<ConversationEvent[], string>({
      query: (conversationUuid) => ({
        url: `/api/messaging/conversations/${conversationUuid}/events/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, conversationUuid) => [
        { type: 'Conversation', id: conversationUuid },
      ],
    }),

    /**
     * The recapture case behind a conversation — the cancelled stay we are trying
     * to win back.
     *
     * There is no "case by conversation" detail route, so this filters the case
     * list and unwraps the single row. A conversation can exist without a case
     * (a guest who wrote in on their own), hence `null` rather than an error.
     */
    getCase: builder.query<RecaptureCase | null, string>({
      query: (conversationUuid) => ({
        url: '/api/recapture/cases/',
        method: 'GET',
        params: { conversation: conversationUuid },
      }),
      transformResponse: (response: PaginatedResponse<RecaptureCase>) =>
        response.results[0] ?? null,
      providesTags: (_result, _error, conversationUuid) => [{ type: 'Case', id: conversationUuid }],
    }),
  }),
});

export const {
  useListConversationsQuery,
  useGetConversationSummaryQuery,
  useGetRecaptureFunnelQuery,
  useGetConversationQuery,
  useListMessagesQuery,
  useSendMediaMessageMutation,
  useSendMessageMutation,
  useSetConversationAgentMutation,
  useMarkReadMutation,
  useMarkUnreadMutation,
  useArchiveConversationMutation,
  useUnarchiveConversationMutation,
  useListNotesQuery,
  useCreateNoteMutation,
  useUpdateNoteMutation,
  useDeleteNoteMutation,
  useGetCaseQuery,
  useListConversationEventsQuery,
} = conversationsApi;
